import BaseModal from 'components/Modals/BaseModal';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { MAX_REASON_LENGTH } from '../config/ServiceManager.config';
import type { PendingToggle } from '../hooks/Deployments.hooks';

interface ServiceDisableModalProps {
  pending: PendingToggle | null;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/**
 * Confirmation for turning a service off.
 *
 * Enabling gets no dialog: it is undone by turning it back on. Disabling is
 * different in two ways an operator has to be told before they agree:
 *
 *   1. the process stops immediately, inside the request rather than on some
 *      later tick, and
 *   2. it cannot be hand-started back. The supervisor treats a running process
 *      whose service flag is false as one to reap, so a manual start does not
 *      survive the next tick. Re-enabling the flag is the only way back.
 *
 * The second point is the one people learn the hard way, which is why it is in
 * the dialog rather than in a document nobody opens.
 */
export default function ServiceDisableModal({
  pending,
  onConfirm,
  onCancel,
}: ServiceDisableModalProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    setReason('');
  }, [pending]);

  if (!pending) return null;

  const { target } = pending;
  const scopeLabel =
    target.scope === 'global' ? 'for every deployment on this supervisor' : `for ${target.wpLabel}`;

  return (
    <BaseModal
      show={!!pending}
      handleShow={onCancel}
      size="md"
      title="Turn this service off?"
      stackLevel={2}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => onConfirm(reason.trim())}>
            Turn off
          </Button>
        </>
      }
    >
      <div className="text-theme">
        <p className="mb-3">
          Turn <span className="fw-semibold">{target.name}</span> off {scopeLabel}?
        </p>
        <ul className="mb-3 ps-3">
          <li className="mb-2">
            The process <span className="fw-semibold">stops immediately</span>. The supervisor stops
            it as part of this request, not on a later tick.
          </li>
          <li>
            The supervisor <span className="fw-semibold">will not start it again</span>, and its
            next restart stops and removes any process whose service is turned off. Turning this
            switch back on is the way to run it again.
          </li>
        </ul>

        <Form.Group controlId="service-disable-reason">
          <Form.Label className="text-theme-muted">
            Reason (optional, recorded in command history)
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            maxLength={MAX_REASON_LENGTH}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Freeing the port for a rebuild"
            autoFocus
          />
          <div className="text-end text-theme-muted small mt-1">
            {reason.length} / {MAX_REASON_LENGTH}
          </div>
        </Form.Group>
      </div>
    </BaseModal>
  );
}
